/**
 * The state behind a sortable table header: which column, which direction, and the page
 * reset that has to happen when either changes.
 *
 * Five pages had written this by hand and the copies had drifted in two ways that matter:
 *
 * - **The page reset.** Changing the sort while on page 3 leaves you on page 3 of an
 *   ordering that no longer exists. Four copies called `setPage(1)`; `ConstructionDeadlines`
 *   did not, and re-sorting from a later page dropped you somewhere arbitrary.
 * - **The first-click direction.** Clicking a *date* column and getting oldest-first is
 *   almost never what someone means. `OwnershipChanges` and `DoNotContact` each encoded
 *   "dates start desc, text starts asc" by hand, as a literal comparison against one key
 *   name; the other three always started `asc`. `descFirst` makes it a declaration.
 *
 * Deliberately NOT a data-fetching hook. It owns three pieces of state and a toggle rule,
 * nothing else — the caller still decides what to do when they change, which on every
 * current page means passing them to the server and refetching. Bundling the fetch in
 * would make this the fourth thing that knows how a list page loads.
 */
import { useCallback } from 'react'
import { useSessionState } from './useSessionState'

export type SortOrder = 'asc' | 'desc'

export interface UseTableSortOptions<K extends string> {
  /**
   * Keys whose first click should sort descending — dates, counts, "most recent".
   * A second click on the same key still toggles.
   */
  descFirst?: readonly K[]
  /**
   * Called whenever the sort changes, to reset paging. Required rather than optional:
   * forgetting it is the bug this hook exists to stop, so it has to be a decision the
   * caller makes out loud. Pass `() => {}` for a table that genuinely does not paginate.
   */
  onSortChange: () => void
  /**
   * Remember the sort for this tab, like the page's filters (useSessionState).
   * `keys` are the valid sort keys, so a stale remembered key falls back to the default.
   */
  persist?: { key: string; keys: readonly K[] }
}

export interface TableSort<K extends string> {
  sortKey: K
  sortOrder: SortOrder
  /** Toggle if it is the current key, otherwise switch and pick the opening direction. */
  handleSort: (key: K) => void
  /** For restoring a saved view; does not fire `onSortChange`. */
  setSort: (key: K, order: SortOrder) => void
}

export function useTableSort<K extends string>(
  defaultKey: K,
  defaultOrder: SortOrder,
  options: UseTableSortOptions<K>,
): TableSort<K> {
  const { descFirst = [], onSortChange, persist } = options
  const [sortKey, setSortKey] = useSessionState<K>(
    persist ? `${persist.key}.sortKey` : null, defaultKey,
    (v) => !!persist && persist.keys.includes(v as K),
  )
  const [sortOrder, setSortOrder] = useSessionState<SortOrder>(
    persist ? `${persist.key}.sortOrder` : null, defaultOrder,
    (v) => v === 'asc' || v === 'desc',
  )

  const handleSort = useCallback(
    (key: K) => {
      if (key === sortKey) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortKey(key)
        setSortOrder(descFirst.includes(key) ? 'desc' : 'asc')
      }
      onSortChange()
    },
    // `descFirst` is expected to be a module-level constant; listing it would re-create
    // the callback on every render for any caller that inlines the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortKey, onSortChange, setSortKey, setSortOrder],
  )

  const setSort = useCallback((key: K, order: SortOrder) => {
    setSortKey(key)
    setSortOrder(order)
  }, [setSortKey, setSortOrder])

  return { sortKey, sortOrder, handleSort, setSort }
}
